import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faFacebookF } from '@fortawesome/free-brands-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { socialLogin } from '../../services/auth';
import { encrypt } from '../../utils/crypto';
import { rememberReturnTo, safeReturnPath } from '../../utils/authRedirect';
import { useAuth } from '../../contexts/AuthContext';
import { GOOGLE_CLIENT_ID, FACEBOOK_APP_ID } from '../../config/env';

const loadScript = (src, id) =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });

/**
 * @param {'login'|'register'} mode
 */
/**
 * layout: 'stacked' (varsayılan, alt alta) | 'row' (yan yana, sade outlined)
 * divider: 'top' (varsayılan) | 'bottom' | 'none'; dividerText ile metin değiştirilebilir
 */
const SocialAuthButtons = ({ mode = 'login', layout = 'stacked', divider = 'top', dividerText, returnTo }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [socialLoading, setSocialLoading] = useState(null);
  const tokenClientRef = useRef(null);

  const googleConfigured = GOOGLE_CLIENT_ID && !GOOGLE_CLIENT_ID.startsWith('YOUR_');
  const facebookConfigured = FACEBOOK_APP_ID && !FACEBOOK_APP_ID.startsWith('YOUR_');

  const finishSocial = useCallback(
    async (provider, token) => {
      setSocialLoading(provider);
      try {
        const response = await socialLogin(provider, token);
        if (response?.statusCode !== 200 || !response.data) {
          toast.error(response?.message || t('auth.login.errors.social'));
          return;
        }

        if (response.data.needsProfileCompletion) {
          navigate('/social-complete', {
            state: {
              provider,
              token,
              email: response.data.email,
              name: response.data.name,
              lastName: response.data.lastName,
              // Dönüş adresi profil tamamlama → etkinleştirme adımlarına taşınır
              from: safeReturnPath(returnTo) || undefined,
            },
          });
          return;
        }

        const authToken = response.data.token;
        if (!authToken) {
          toast.error(t('auth.login.errors.general'));
          return;
        }

        localStorage.removeItem('activationTimer');
        localStorage.removeItem('activationTimerExpiry');
        const encryptedToken = encrypt(authToken);
        localStorage.setItem('authToken', encryptedToken || authToken);

        setUser({
          id: response.data.id,
          firstName: response.data.name,
          lastName: response.data.lastName,
          isActivated: false,
        });
        toast.success(
          mode === 'register' ? t('auth.socialComplete.success') : t('auth.login.success')
        );
        const from = safeReturnPath(returnTo);
        rememberReturnTo(from);
        navigate('/activation', { state: from ? { from } : undefined });
      } catch (error) {
        toast.error(t('auth.login.errors.social'));
      } finally {
        setSocialLoading(null);
      }
    },
    [mode, navigate, returnTo, setUser, t]
  );

  useEffect(() => {
    if (!googleConfigured) return;
    let cancelled = false;
    loadScript('https://accounts.google.com/gsi/client', 'google-gsi')
      .then(() => {
        if (cancelled || !window.google?.accounts) return;

        // ID token (credential) — backend JWT doğrular
        if (window.google.accounts.id) {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (resp) => {
              if (resp?.credential) {
                finishSocial('google', resp.credential);
              } else {
                toast.error(t('auth.login.errors.socialCancelled'));
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm_for_prompt: true,
          });
        }

        // Fallback: access_token (userinfo)
        if (window.google.accounts.oauth2) {
          tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            callback: (resp) => {
              if (resp?.access_token) {
                finishSocial('google', resp.access_token);
              } else {
                toast.error(t('auth.login.errors.socialCancelled'));
              }
            },
            error_callback: () => {
              toast.error(t('auth.login.errors.socialCancelled'));
            },
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [finishSocial, googleConfigured, t]);

  useEffect(() => {
    if (!facebookConfigured) return;
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v19.0',
      });
    };
    loadScript('https://connect.facebook.net/tr_TR/sdk.js', 'facebook-jssdk').catch(() => {});
  }, [facebookConfigured]);

  const handleGoogle = () => {
    if (!googleConfigured) {
      toast.error(t('auth.login.errors.socialNotConfigured'));
      return;
    }

    // Önce One Tap / FedCM credential dene; olmazsa OAuth popup
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt((notification) => {
        const failed =
          notification?.isNotDisplayed?.() ||
          notification?.isSkippedMoment?.() ||
          notification?.isDismissedMoment?.();
        if (failed) {
          if (!tokenClientRef.current) {
            toast.error(t('auth.login.errors.social'));
            return;
          }
          tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
        }
      });
      return;
    }

    if (!tokenClientRef.current) {
      toast.error(t('auth.login.errors.social'));
      return;
    }
    tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
  };

  const handleFacebook = () => {
    if (!facebookConfigured || !window.FB) {
      toast.error(t('auth.login.errors.socialNotConfigured'));
      return;
    }
    window.FB.login(
      (response) => {
        const accessToken = response?.authResponse?.accessToken;
        if (accessToken) {
          finishSocial('facebook', accessToken);
        } else {
          toast.error(t('auth.login.errors.socialCancelled'));
        }
      },
      { scope: 'email,public_profile' }
    );
  };

  const googleLabel =
    mode === 'register' ? t('auth.social.registerGoogle') : t('auth.social.continueGoogle');
  const facebookLabel =
    mode === 'register' ? t('auth.social.registerFacebook') : t('auth.social.continueFacebook');
  const dividerLabel =
    dividerText || (mode === 'register' ? t('auth.social.orRegister') : t('auth.social.orContinue'));

  const dividerEl = (
    <Divider sx={{ my: 2.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {dividerLabel}
      </Typography>
    </Divider>
  );

  if (layout === 'row') {
    const rowBtn = {
      py: 1.2,
      borderRadius: '10px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9rem',
      color: 'text.primary',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      '&:hover': { borderColor: 'text.disabled', backgroundColor: 'action.hover' },
    };
    return (
      <Box sx={{ width: '100%' }}>
        {divider === 'top' ? dividerEl : null}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
          <Button
            fullWidth
            variant="outlined"
            disabled={!!socialLoading}
            onClick={handleGoogle}
            startIcon={
              socialLoading === 'google' ? <CircularProgress size={18} /> : <FontAwesomeIcon icon={faGoogle} style={{ color: '#EA4335' }} />
            }
            sx={rowBtn}
          >
            Google
          </Button>
          <Button
            fullWidth
            variant="outlined"
            disabled={!!socialLoading}
            onClick={handleFacebook}
            startIcon={
              socialLoading === 'facebook' ? <CircularProgress size={18} /> : <FontAwesomeIcon icon={faFacebookF} style={{ color: '#1877F2' }} />
            }
            sx={rowBtn}
          >
            Facebook
          </Button>
        </Box>
        {divider === 'bottom' ? dividerEl : null}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      {divider === 'top' ? dividerEl : null}

      <Button
        fullWidth
        variant="outlined"
        disabled={!!socialLoading}
        onClick={handleGoogle}
        startIcon={
          socialLoading === 'google' ? (
            <CircularProgress size={18} />
          ) : (
            <FontAwesomeIcon icon={faGoogle} style={{ color: '#EA4335' }} />
          )
        }
        sx={{
          mb: 1.5,
          py: 1.35,
          borderRadius: 2.5,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.95rem',
          color: 'text.primary',
          borderColor: '#DADCE0',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          '&:hover': {
            borderColor: '#BDBDBD',
            backgroundColor: 'action.hover',
          },
        }}
      >
        {googleLabel}
      </Button>

      <Button
        fullWidth
        variant="contained"
        disabled={!!socialLoading}
        onClick={handleFacebook}
        startIcon={
          socialLoading === 'facebook' ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            <FontAwesomeIcon icon={faFacebookF} />
          )
        }
        sx={{
          py: 1.35,
          borderRadius: 2.5,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.95rem',
          backgroundColor: '#1877F2',
          boxShadow: '0 2px 8px rgba(24,119,242,0.35)',
          '&:hover': {
            backgroundColor: '#166FE5',
          },
        }}
      >
        {facebookLabel}
      </Button>
      {divider === 'bottom' ? dividerEl : null}
    </Box>
  );
};

export default SocialAuthButtons;
